import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import {
   getClientAccessApi,
   createClientAccessApi,
   updateClientAccessApi,
   deleteClientAccessApi,
} from '@/api/vendo';

/**
 * "Sub login" — the second hand-off option next to a full transfer
 * (Ray 2026-07-08): the seller KEEPS ownership and gives the buyer a
 * simple login for the basic portal (client.mysmartfi.ph) where they
 * manage only this device (rates, settings, sales). Made for buyers in
 * remote areas with no email.
 */

const CLIENT_PORTAL_URL = 'https://client.mysmartfi.ph';

const ClientAccessSection: React.FC<{
   deviceId: string;
   sellerEmail: string;
}> = ({ deviceId, sellerEmail }) => {
   const queryClient = useQueryClient();
   const { data, isLoading } = useQuery({
      queryKey: ['client-access', deviceId],
      queryFn: () => getClientAccessApi(deviceId),
   });
   const access = data?.access ?? null;

   const [busy, setBusy] = useState(false);
   // Plaintext password — shown exactly once after create/reset.
   const [freshPassword, setFreshPassword] = useState<string | null>(null);
   const [editingName, setEditingName] = useState(false);
   const [nameDraft, setNameDraft] = useState('');
   const [confirmDelete, setConfirmDelete] = useState(false);

   const refresh = () =>
      queryClient.invalidateQueries({ queryKey: ['client-access', deviceId] });

   const run = async (fn: () => Promise<void>) => {
      setBusy(true);
      try {
         await fn();
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Something went wrong');
      } finally {
         setBusy(false);
      }
   };

   const handleCreate = () =>
      run(async () => {
         const res = await createClientAccessApi(deviceId);
         setFreshPassword(res.password);
         refresh();
         toast.success('Sub login created');
      });

   const handleResetPassword = () =>
      run(async () => {
         const res = await updateClientAccessApi(deviceId, { resetPassword: true });
         if (res.password) setFreshPassword(res.password);
         refresh();
         toast.success('New password generated');
      });

   // Buyer forgot the password → the seller reviews the CURRENT one and
   // resends it (no reset needed, the buyer's saved login keeps working).
   const handleShowPassword = () =>
      run(async () => {
         const res = await getClientAccessApi(deviceId, { reveal: true });
         if (res.access?.password) setFreshPassword(res.access.password);
         else toast.error('Could not read the password — generate a new one');
      });

   const handleToggleActive = () =>
      run(async () => {
         await updateClientAccessApi(deviceId, { active: !access?.active });
         refresh();
      });

   const handleRename = () =>
      run(async () => {
         await updateClientAccessApi(deviceId, { username: nameDraft });
         setEditingName(false);
         refresh();
         toast.success('Username updated');
      });

   const handleDelete = () =>
      run(async () => {
         await deleteClientAccessApi(deviceId);
         setConfirmDelete(false);
         setFreshPassword(null);
         refresh();
         toast.success('Sub login removed');
      });

   const copy = (text: string, label: string) => {
      navigator.clipboard?.writeText(text);
      toast.success(`${label} copied`);
   };

   if (isLoading) return null;

   return (
      <Card>
         <Heading>🔑 Sub login (keep ownership)</Heading>
         <Text>
            No buyer email? Create a simple login instead of a transfer.
            You stay the owner; the buyer manages ONLY this device — rates,
            settings and sales — at{' '}
            <PortalLink
               href={CLIENT_PORTAL_URL}
               target="_blank"
               rel="noreferrer"
            >
               client.mysmartfi.ph
            </PortalLink>
            .
         </Text>

         {!access && (
            <CreateBtn type="button" onClick={handleCreate} disabled={busy}>
               {busy ? 'Creating…' : 'Create sub login'}
            </CreateBtn>
         )}

         {access && (
            <>
               <CredBox>
                  <CredRow>
                     <CredLabel>Your email</CredLabel>
                     <CredValue>{sellerEmail}</CredValue>
                     <CopyBtn type="button" onClick={() => copy(sellerEmail, 'Email')}>
                        Copy
                     </CopyBtn>
                  </CredRow>
                  <CredRow>
                     <CredLabel>Username</CredLabel>
                     {editingName ? (
                        <>
                           <CredInput
                              value={nameDraft}
                              onChange={(e) => setNameDraft(e.target.value)}
                              maxLength={40}
                              autoFocus
                           />
                           <CopyBtn type="button" onClick={handleRename} disabled={busy}>
                              Save
                           </CopyBtn>
                           <GhostSmall type="button" onClick={() => setEditingName(false)}>
                              Cancel
                           </GhostSmall>
                        </>
                     ) : (
                        <>
                           <CredValue>{access.username}</CredValue>
                           <CopyBtn
                              type="button"
                              onClick={() => copy(access.username, 'Username')}
                           >
                              Copy
                           </CopyBtn>
                           <GhostSmall
                              type="button"
                              onClick={() => {
                                 setNameDraft(access.username);
                                 setEditingName(true);
                              }}
                           >
                              Edit
                           </GhostSmall>
                        </>
                     )}
                  </CredRow>
                  <CredRow>
                     <CredLabel>Password</CredLabel>
                     {freshPassword ? (
                        <>
                           <CredValue $highlight>{freshPassword}</CredValue>
                           <CopyBtn
                              type="button"
                              onClick={() => copy(freshPassword, 'Password')}
                           >
                              Copy
                           </CopyBtn>
                        </>
                     ) : (
                        <>
                           <CredValue>••••••••</CredValue>
                           <CopyBtn
                              type="button"
                              onClick={handleShowPassword}
                              disabled={busy}
                           >
                              Show
                           </CopyBtn>
                           <GhostSmall
                              type="button"
                              onClick={handleResetPassword}
                              disabled={busy}
                           >
                              Generate new
                           </GhostSmall>
                        </>
                     )}
                  </CredRow>
                  {freshPassword && (
                     <OnceNote>
                        Save this password now — it is shown only once. Send all
                        three lines to your buyer.
                     </OnceNote>
                  )}
               </CredBox>

               {/* What the buyer may change — seller-controlled switches. */}
               <PermRow>
                  <PermLabel>Buyer can:</PermLabel>
                  <PermToggle
                     type="button"
                     $on={access.permissions?.renameVendo !== false}
                     disabled={busy}
                     onClick={() =>
                        run(async () => {
                           await updateClientAccessApi(deviceId, {
                              permissions: {
                                 renameVendo: !(access.permissions?.renameVendo !== false),
                              },
                           });
                           refresh();
                        })
                     }
                  >
                     Rename vendo
                  </PermToggle>
                  <PermToggle
                     type="button"
                     $on={access.permissions?.renameSsid !== false}
                     disabled={busy}
                     onClick={() =>
                        run(async () => {
                           await updateClientAccessApi(deviceId, {
                              permissions: {
                                 renameSsid: !(access.permissions?.renameSsid !== false),
                              },
                           });
                           refresh();
                        })
                     }
                  >
                     Rename WiFi (SSID)
                  </PermToggle>
               </PermRow>
               <ActionRow>
                  <GhostSmall type="button" onClick={handleToggleActive} disabled={busy}>
                     {access.active ? 'Disable login' : 'Enable login'}
                  </GhostSmall>
                  {!access.active && <InactiveTag>disabled</InactiveTag>}
                  <Spacer />
                  {confirmDelete ? (
                     <>
                        <DangerSmall type="button" onClick={handleDelete} disabled={busy}>
                           Yes, remove it
                        </DangerSmall>
                        <GhostSmall type="button" onClick={() => setConfirmDelete(false)}>
                           Keep
                        </GhostSmall>
                     </>
                  ) : (
                     <DangerGhost type="button" onClick={() => setConfirmDelete(true)}>
                        Remove sub login
                     </DangerGhost>
                  )}
               </ActionRow>
            </>
         )}
      </Card>
   );
};

export default ClientAccessSection;

const Card = styled.div`
   margin-top: 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 16px;
`;

const Heading = styled.h4`
   font-size: 14px;
   font-weight: 700;
`;

const Text = styled.p`
   margin-top: 6px;
   font-size: 12.5px;
   color: ${colors.GRAY2};
   line-height: 1.5;
`;

const PortalLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 600;
   text-decoration: underline;
`;

const CreateBtn = styled.button`
   margin-top: 12px;
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
   border-radius: 8px;
   padding: 9px 16px;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const CredBox = styled.div`
   margin-top: 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 10px 12px;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const CredRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const CredLabel = styled.span`
   width: 84px;
   flex-shrink: 0;
   font-size: 11.5px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;

const CredValue = styled.span<{ $highlight?: boolean }>`
   font-size: 13px;
   font-weight: 700;
   font-family: ui-monospace, monospace;
   color: ${({ $highlight }) => ($highlight ? colors.SUCCESS : 'inherit')};
   overflow-wrap: anywhere;
`;

const CredInput = styled.input`
   flex: 1;
   min-width: 120px;
   padding: 6px 9px;
   border-radius: 7px;
   border: 1.5px solid ${colors.GRAY12};
   font-size: 13px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const CopyBtn = styled.button`
   border: 1px solid ${colors.PRIMARY};
   background: transparent;
   color: ${colors.PRIMARY};
   border-radius: 7px;
   padding: 4px 10px;
   font-size: 11.5px;
   font-weight: 600;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const GhostSmall = styled.button`
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: ${colors.GRAY2};
   border-radius: 7px;
   padding: 4px 10px;
   font-size: 11.5px;
   font-weight: 600;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const DangerGhost = styled(GhostSmall)`
   color: ${colors.DANGER};
   border-color: ${colors.DANGER}55;
`;

const DangerSmall = styled.button`
   border: none;
   background: ${colors.DANGER};
   color: #fff;
   border-radius: 7px;
   padding: 4px 12px;
   font-size: 11.5px;
   font-weight: 700;
   cursor: pointer;
`;

const OnceNote = styled.div`
   font-size: 11.5px;
   color: ${colors.SUCCESS};
   font-weight: 600;
`;

const ActionRow = styled.div`
   margin-top: 10px;
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const InactiveTag = styled.span`
   font-size: 10.5px;
   font-weight: 700;
   text-transform: uppercase;
   color: ${colors.DANGER};
`;

const Spacer = styled.div`
   flex: 1;
`;

const PermRow = styled.div`
   margin-top: 10px;
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;

const PermLabel = styled.span`
   font-size: 11.5px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;

const PermToggle = styled.button<{ $on: boolean }>`
   border: 1.5px solid ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY12)};
   background: ${({ $on }) => ($on ? `${colors.SUCCESS}14` : 'transparent')};
   color: ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY3)};
   border-radius: 999px;
   padding: 4px 12px;
   font-size: 11.5px;
   font-weight: 700;
   cursor: pointer;
   &:disabled { opacity: 0.6; }
   &::before { content: '${({ $on }) => ($on ? "✓ " : "✕ ")}'; }
`;
